// ============================================================================
// @orgazone/date-format — rendu HUMAIN des dates et heures, en français.
//
// ⚠️ SATELLITE DE PRÉSENTATION, calqué sur `@orgazone/money-format` : ici on AFFICHE, on ne calcule pas.
//
// ⚠️ ZÉRO `Intl` — DÉLIBÉRÉ, pour deux raisons :
//   1. `Intl` n'est pas fiable sous Hermes (React Native) — c'est déjà le motif retenu par
//      `money-format`, et la tablette porte d'ailleurs DÉJÀ un formateur manuel de dates
//      (`settings-tabs.tsx`) à côté d'appels `toLocaleDateString('fr-FR')` : deux approches dans la
//      même app, dont une qui peut rendre une date au format américain sur l'appareil.
//   2. Le rendu doit être IDENTIQUE partout. `Intl` dépend du moteur (Hermes / JSC / navigateur) et des
//      données ICU embarquées : deux plateformes peuvent afficher deux choses. Un formatage explicite,
//      non.
//
// ⚠️ POURQUOI CE PAQUET EXISTE : 46 appels `toLocale*` écrits à la main dans les apps, pour **15 formats
// différents** de la même chose — `04/08`, `04/08/26`, `4 août`, `mar. 4 août 2026`, `4 août 2026 à 14:32`…
// Une date ne devrait pas changer d'apparence selon l'écran où on la regarde.
//
// FUSEAU : tout est rendu en heure LOCALE (celle de l'appareil), comme les appels qu'il remplace.
// ============================================================================

/** Ce qu'on sait afficher : une Date, un ISO 8601, un epoch ms — ou rien. */
export type DateLike = Date | string | number | null | undefined;

/** Rendu d'une valeur absente. ⚠️ Un tiret cadratin, JAMAIS une chaîne vide : une cellule vide se lit
 *  comme un bug d'affichage, un tiret se lit comme « pas de valeur » (§23.0). */
export const EMPTY = '—';

const MONTHS_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const MONTHS_LONG = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const WEEKDAYS_SHORT = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
const WEEKDAYS_LONG = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

const p2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

/**
 * Normalise l'entrée. Renvoie `null` si la valeur est absente OU invalide.
 * ⚠️ Une date invalide (`new Date('n'importe quoi')`) rend `null` et donc le tiret, plutôt que
 * « Invalid Date » à l'écran — le symptôme serait illisible pour un opérateur.
 */
function toDate(v: DateLike): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Applique un rendu à une date valide, ou renvoie le tiret. */
const fmt = (v: DateLike, f: (d: Date) => string): string => {
  const d = toDate(v);
  return d ? f(d) : EMPTY;
};

// ── DATES ───────────────────────────────────────────────────────────────────

/** `04/08/2026` — le format par défaut d'une date en France. */
export const date = (v: DateLike): string => fmt(v, (d) => `${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${d.getFullYear()}`);

/** `04/08` — quand l'année est évidente (fenêtre courante, échéancier). */
export const dateShort = (v: DateLike): string => fmt(v, (d) => `${p2(d.getDate())}/${p2(d.getMonth() + 1)}`);

/** `04/08/26` — colonnes denses où la place manque. */
export const dateCompact = (v: DateLike): string => fmt(v, (d) => `${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${p2(d.getFullYear() % 100)}`);

/** `4 août 2026` — texte courant, plus lisible qu'une suite de chiffres. */
export const dateLong = (v: DateLike): string => fmt(v, (d) => `${d.getDate()} ${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`);

/** `4 juil. 2026` — le mois abrégé : une colonne de tableau, où le mois en toutes lettres prendrait trop de place. */
export const dateMedium = (v: DateLike): string => fmt(v, (d) => `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`);

/** `4 août` — mois en toutes lettres sans l'année. */
export const dateLongNoYear = (v: DateLike): string => fmt(v, (d) => `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`);

/** `mar. 4 août 2026` — quand le JOUR DE LA SEMAINE porte du sens (planification, clôture). */
export const dateWithWeekday = (v: DateLike): string =>
  fmt(v, (d) => `${WEEKDAYS_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`);

/** `jeudi 30 juillet 2026` — en-tête d'écran, où la date se LIT au lieu de se déchiffrer. */
export const dateWithWeekdayLong = (v: DateLike): string =>
  fmt(v, (d) => `${WEEKDAYS_LONG[d.getDay()]} ${d.getDate()} ${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`);

/** `mar.` — le jour seul. */
export const weekday = (v: DateLike): string => fmt(v, (d) => WEEKDAYS_SHORT[d.getDay()]!);

// ── HEURES ──────────────────────────────────────────────────────────────────

/** `14:32` — toujours sur 24 h : le format 12 h n'a pas cours ici et introduit une ambiguïté. */
export const time = (v: DateLike): string => fmt(v, (d) => `${p2(d.getHours())}:${p2(d.getMinutes())}`);

/** `14:32:07` — quand la seconde compte (journal, horodatage d'échange). */
export const timeSec = (v: DateLike): string =>
  fmt(v, (d) => `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`);

// ── DATE + HEURE ────────────────────────────────────────────────────────────

/** `04/08/2026 14:32` — l'horodatage courant d'un tableau. */
export const dateTime = (v: DateLike): string => fmt(v, (d) => `${date(d)} ${time(d)}`);

/** `04/08/2026 14:32:07` — quand la seconde compte dans un horodatage complet (sessions, audit). */
export const dateTimeSec = (v: DateLike): string => fmt(v, (d) => `${date(d)} ${timeSec(d)}`);

/** `04/08/26 14:32` — variante dense. */
export const dateTimeCompact = (v: DateLike): string => fmt(v, (d) => `${dateCompact(d)} ${time(d)}`);

/** `04/08 14:32` — fenêtre courante, l'année étant évidente. */
export const dateTimeShort = (v: DateLike): string => fmt(v, (d) => `${dateShort(d)} ${time(d)}`);

/** `4 août 2026 à 14:32` — texte courant (fiche, détail). */
export const dateTimeLong = (v: DateLike): string => fmt(v, (d) => `${dateLong(d)} à ${time(d)}`);

// ── DURÉE ÉCOULÉE ───────────────────────────────────────────────────────────

/**
 * `il y a 4 min` — quand la question est « est-ce récent ? » et non « c'était quand ? ».
 *
 * ⚠️ EXTRAIT DE DEUX COPIES (2026-08-18, règle de 2 §24.3) : la Console (`sync-health.ts`) et le store
 * privé (`Backoffice.tsx`) en portaient chacun une, et elles DIVERGEAIENT déjà — l'une plancher à
 * 1 minute, l'autre affichait « il y a 0 min ». Le module d'origine disait lui-même pourquoi : « deux
 * copies d'un même jugement finissent par diverger ». Le tableau de bord aurait été la troisième.
 *
 * ⚠️ LE PLANCHER D'UNE MINUTE EST VOULU : « il y a 0 min » se lit comme une valeur non calculée.
 * ⚠️ L'ARRONDI DÉCIDE DU PALIER, et pas la valeur brute : sans ça, 47 h 40 rendait « il y a 48 h » —
 * une durée exprimée dans une unité que le palier suivant venait justement d'abandonner.
 * ⚠️ `now` est INJECTABLE pour être testable : une fonction qui lit l'horloge elle-même ne se vérifie
 * qu'en pilotant le temps de tout le processus.
 */
export function timeAgo(v: DateLike, now: DateLike = new Date()): string {
  const d = toDate(v);
  const ref = toDate(now);
  if (!d || !ref) return EMPTY;
  // ⚠️ Un instant dans le FUTUR (horloge décalée) retombe sur le plancher plutôt que sur « il y a
  // −3 min » : l'écart d'horloge n'est pas une information que l'opérateur peut exploiter.
  const min = Math.max(1, Math.round((ref.getTime() - d.getTime()) / 60_000));
  if (min < 60) return `il y a ${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `il y a ${h} h`;
  return `il y a ${Math.round(h / 24)} j`;
}
